import { useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { usePipeline } from "../../contexts/PipelineContext";
import AppointmentsContent from "../../components/activities/appointments/AppointmentsContent";
import ServiceRequestContent from "../../components/activities/serviceRequests/ServiceRequestContent";
import ListingRequestContent from "../../components/activities/listingRequests/ListingRequestContent";

const ActivitiesContainer = () => {
  const {
    defaultPipelinePositions,
    maintenancePipelinePositions,
    generalPipelinePositions,
    sourcePropertiesPipelinePositions,
  } = usePipeline();

  // Determine which tabs to show based on pipeline access
  const hasAppointments = useMemo(() => {
    return !!defaultPipelinePositions && Object.keys(defaultPipelinePositions).length > 0;
  }, [defaultPipelinePositions]);

  const hasMaintenance = useMemo(() => {
    return !!maintenancePipelinePositions && Object.keys(maintenancePipelinePositions).length > 0;
  }, [maintenancePipelinePositions]);

  const hasServiceRequests = useMemo(() => {
    return !!generalPipelinePositions && Object.keys(generalPipelinePositions).length > 0;
  }, [generalPipelinePositions]);

  const hasListingRequests = useMemo(() => {
    return (
      !!sourcePropertiesPipelinePositions &&
      Object.keys(sourcePropertiesPipelinePositions).length > 0
    );
  }, [sourcePropertiesPipelinePositions]);

  // Build tabs array based on available pipelines
  const tabs = useMemo(() => {
    const availableTabs: Array<{
      label: string;
      type: "appointments" | "maintenance" | "serviceRequests" | "listingRequests";
    }> = [];

    if (hasAppointments) {
      availableTabs.push({ label: "Citas", type: "appointments" });
    }
    if (hasMaintenance) {
      availableTabs.push({ label: "Mantenimiento", type: "maintenance" });
    }
    if (hasServiceRequests) {
      availableTabs.push({ label: "Solicitudes de Servicio", type: "serviceRequests" });
    }
    if (hasListingRequests) {
      availableTabs.push({ label: "Captación de Inmuebles", type: "listingRequests" });
    }

    return availableTabs;
  }, [hasAppointments, hasMaintenance, hasServiceRequests, hasListingRequests]);

  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

  // Use the first available tab as default
  const currentTab = activeTab || tabs[0]?.type;

  // If no tabs are available, show a message
  if (tabs.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        No tienes acceso a ninguna actividad
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100dvh-60px)] flex-col overflow-hidden bg-[#F9F9FB] md:h-screen">
      <Tabs
        value={currentTab}
        onValueChange={setActiveTab}
        className="flex flex-1 flex-col overflow-hidden"
      >
        <TabsList
          className="overflow-x-auto border-b border-[#DFDEE2] bg-white px-2 md:px-8"
          variant="line"
        >
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.type}
              value={tab.type}
              className="whitespace-nowrap text-sm md:text-base"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {tabs.map((tab) => (
          <TabsContent
            key={tab.type}
            value={tab.type}
            className="mt-0 flex-1 overflow-hidden"
          >
            {tab.type === "appointments" && <AppointmentsContent key="appointments" />}
            {tab.type === "maintenance" && <ServiceRequestContent key="maintenance" pipelineId="maintenance" />}
            {tab.type === "serviceRequests" && <ServiceRequestContent key="serviceRequests" />}
            {tab.type === "listingRequests" && <ListingRequestContent key="listingRequests" />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default ActivitiesContainer;
